import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { faChevronRight, faPlusCircle, faRotate, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, first, Observable } from 'rxjs';
import { indicate } from 'src/app/operators';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { MatPaginatorGoToComponent } from 'src/app/shared/components/mat-paginator-go-to/mat-paginator-go-to.component';
import { EsraPipingAPIClient, FileResponse, UserViewModel } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import { AddUserDialogComponent } from './add-user-dialog/add-user-dialog.component';

/**
 * Read-only user list backing the "administration / user management" module.
 * Only calls `GET /api/User/list`, which never triggers an Azure Graph refresh -- safe to
 * call on every page load. Per-user Graph refresh is offered explicitly from the detail screen.
 */
@Component({
    selector: 'app-user-management-list',
    templateUrl: './user-management-list.component.html',
    styleUrls: ['./user-management-list.component.scss'],
    standalone: false
})
export class UserManagementListComponent implements OnInit {
  private readonly esraApiClient = inject(EsraPipingAPIClient);
  private readonly snackBarService = inject(SnackBarService);
  private readonly insightsService = inject(InsightsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly dataSource: MatTableDataSource<UserViewModel> = new MatTableDataSource<UserViewModel>();
  readonly displayedColumns: string[] = ['displayName', 'email', 'location', 'indicator'];
  readonly pageSizes: number[] = [10, 20, 50, 100];

  readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly syncingAll$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True when the last load attempt failed; drives the error empty state and retry action. */
  loadFailed: boolean = false;
  userCount: number = 0;

  readonly faSearch = faSearch;
  readonly faTimes = faTimes;
  readonly faChevronRight = faChevronRight;
  readonly faRotate = faRotate;
  readonly faPlusCircle = faPlusCircle;

  /** Passed to `app-export-excel-button`; the download filename comes from the response's content-disposition header. */
  exportUsersFn = (): Observable<FileResponse> => this.esraApiClient.exportAllUsersToExcel();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  @ViewChild('paginator') set paginator(paginator: MatPaginatorGoToComponent) {
    if (paginator) {
      this.dataSource.paginator = paginator.paginator;
    }
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  ngOnInit(): void {
    this.dataSource.filterPredicate = (user: UserViewModel, filterText: string): boolean =>
      (user.displayName ?? '').toLowerCase().includes(filterText) ||
      (user.email ?? '').toLowerCase().includes(filterText);

    // Location isn't a direct property; sort by the same combined label shown in the table.
    // Lowercase all values so sorting isn't case-sensitive -- otherwise lowercase-first names
    // (e.g. service accounts like "svc_...") sort out of order against "Zipunnikov", "Zaheer", etc.
    this.dataSource.sortingDataAccessor = (user: UserViewModel, columnName: string): string => {
      const value: string = columnName === 'location' ? this.getLocation(user) : (user[columnName as keyof UserViewModel] as string ?? '');
      return value.toLowerCase();
    };

    this.loadUsers();
  }

  /** Loads the local user list. Does not refresh any user from Azure Graph. */
  loadUsers(): void {
    this.loadFailed = false;

    this.esraApiClient.listUsers().pipe(
      indicate(this.loading$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (users: UserViewModel[]) => {
        this.dataSource.data = users ?? [];
        this.userCount = this.dataSource.data.length;
      },
      error: (err: unknown) => {
        this.loadFailed = true;
        this.snackBarService.showError(getApiErrorMessage(err, 'Failed to load users.'));
        this.insightsService.logException(err as Error);
      },
    });
  }

  setSearchValueAndSearch(event: KeyboardEvent): void {
    const searchText: string = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = searchText;
  }

  clearSearchText(): void {
    this.searchInput.nativeElement.value = '';
    this.dataSource.filter = '';
  }

  openUser(user: UserViewModel): void {
    this.router.navigate(['/administration/user-management', user.id]);
  }

  /** Opens the Azure AD search modal to add one or more new users, then reloads the list when any were added. */
  openAddUserDialog(): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().pipe(first()).subscribe((usersAdded: boolean | undefined) => {
      if (usersAdded) {
        this.loadUsers();
      }
    });
  }

  /** Combines city, state, and country into a single label, omitting any part missing from the user's Azure profile. */
  getLocation(user: UserViewModel): string {
    const parts: string[] = [user.city, user.state, user.country].filter((part: string | undefined): part is string => !!part);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  }

  /** Bulk-refreshes every user's Azure-synchronized attributes. An explicit, confirmed admin maintenance action -- never triggered implicitly. */
  syncAllUsersFromGraph(): void {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: 'Sync all users from Azure AD now? This refreshes every user\'s attributes and may take a while.',
        confirmText: 'Sync all',
        cancelText: 'Cancel',
      },
    });

    confirmDialogRef.afterClosed().pipe(first()).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.esraApiClient.updateAllUsersFromGraph().pipe(
        indicate(this.syncingAll$),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: () => {
          this.snackBarService.showInfo('All users refreshed from Azure AD.');
          this.loadUsers();
        },
        error: (err: unknown) => {
          this.snackBarService.showError(getApiErrorMessage(err, 'Failed to sync all users from Azure AD.'));
          this.insightsService.logException(err as Error);
        },
      });
    });
  }
}
